import { useState, useEffect, useRef, useCallback } from 'react';
import { MapContainer, TileLayer, GeoJSON } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import axios from 'axios';
import { toast } from 'react-toastify';
import { Icon } from '@iconify/react/dist/iconify.js';

// Components
import MapLegend from './MapLegend';
import MapSetBounds from './MapSetBounds';
import MapProvinceLabels from './MapProvinceLabels';
import MapApiPinpoint from './MapApiPinpoint';
import MapFilters from './MapFilters';
import MapClusterControl from './MapClusterControl';
import MapClusterGroup from './MapClusterGroup';

// New component for district labels
const MapDistrictLabels = ({ geojson }) => {
  const onEachFeature = (feature, layer) => {
    if (feature.properties && feature.properties.DISTRICT) {
      const districtName = feature.properties.DISTRICT;
      const center = layer.getBounds().getCenter();
      const label = L.divIcon({
        className: 'district-label',
        html: `<div style="font-size: 12px; font-weight: bold; color: black; text-shadow: 0 0 3px white;">${districtName}</div>`,
        iconSize: [0, 0]
      });
      L.marker(center, { icon: label }).addTo(layer._map);
    }
  };

  return geojson ? <GeoJSON data={geojson} onEachFeature={onEachFeature} style={{ opacity: 0, fillOpacity: 0 }} /> : null;
};

// Fix for default marker icons in Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const MapComponent = () => {
  const API_BASE_URL = process.env.REACT_APP_BE_API_URL;
  const [loading, setLoading] = useState(true);
  const [provinces, setProvinces] = useState(null);
  const [districts, setDistricts] = useState(null);
  const [plantations, setPlantations] = useState([]);
  const [totalPlantations, setTotalPlantations] = useState(0);
  const [totalPlants, setTotalPlants] = useState(0);
  const [apiLoading, setApiLoading] = useState(false);
  const [apiError, setApiError] = useState(null);
  // const [showLabels, setShowLabels] = useState(false);
   const [isClusteringEnabled, setIsClusteringEnabled] = useState(true);
  const [filterOptions, setFilterOptions] = useState({
    provinces: [],
    districts: [],
    categories: [],
    tehsils: [],
    plantationCampaigns: [],
    plantationTypes: [],
    years: [],
    siteTypes: []
  });
  const [filters, setFilters] = useState({
    category: "",
    province: "",
    district: "",
    tehsil: "",
    yearOfPlantation: "",
    plantationCampaign: "",
    plantationType: "",
    siteType: ""
  });
  const mapRef = useRef(null);
  const mapCardRef = useRef(null);
  const mapWrapperRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

   const handleClusteringChange = (enabled) => {
    setIsClusteringEnabled(enabled);
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      mapCardRef.current?.requestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Resize the Leaflet map only once the wrapper's actual box size has changed
  // (e.g. entering/exiting full screen), instead of guessing with a timeout -
  // guessing wrong races Leaflet's canvas renderer mid-redraw and crashes it.
  useEffect(() => {
    const wrapper = mapWrapperRef.current;
    if (!wrapper || typeof ResizeObserver === 'undefined') return;

    let frame;
    const observer = new ResizeObserver(() => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        mapRef.current?.invalidateSize?.();
      });
    });
    observer.observe(wrapper);

    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, []);

  const fetchFilterOptions = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      
      const [
        provincesResponse,
        districtsResponse,
        categoriesResponse,
        tehsilsResponse,
        plantationCampaignsResponse,
        plantationTypesResponse
      ] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/plantation/pafs/get-provinces`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_BASE_URL}/api/plantation/pafs/get-districts`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_BASE_URL}/api/plantation/pafs/get-categories`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_BASE_URL}/api/plantation/pafs/get-tehsils`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_BASE_URL}/api/plantation/pafs/get-plantation-campaign`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_BASE_URL}/api/plantation/pafs/plantation-type`, { headers: { Authorization: `Bearer ${token}` } })
      ]);

      const currentYear = new Date().getFullYear();
      const years = Array.from({ length: 10 }, (_, i) => currentYear - i);
      
      const siteTypes = ['Park', 'Roadside', 'Forest', 'Residential Area', 'Commercial Area'];
      
      setFilterOptions({
        provinces: provincesResponse.data.provinces || [],
        districts: districtsResponse.data.districts || [],
        categories: categoriesResponse.data.categories || [],
        tehsils: tehsilsResponse.data.tehsils || [],
        plantationCampaigns: plantationCampaignsResponse.data.plantationCampaigns || [],
        plantationTypes: plantationTypesResponse.data.plantationTypes || [],
        years,
        siteTypes
      });
    } catch (error) {
      console.error("Error fetching filter options:", error);
      toast.error("Failed to load filter options.");
    }
  }, [API_BASE_URL]);

  const fetchPlantations = useCallback(async () => {
    const token = localStorage.getItem("token");
    
    setApiLoading(true);
    setApiError(null);
    
    try {
      const url = `${API_BASE_URL}/api/plantation/pafs/plantations-cordinates`;
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(filters)
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.status && result.data) {
        setPlantations(result.data);
        
        // Safely handle the total values - convert to numbers
        setTotalPlantations(Number(result.totalPlantations) || 0);
        setTotalPlants(Number(result.totalPlants) || 0);
      } else {
        setApiError('Invalid API response format');
        // Set defaults on error
        setTotalPlantations(0);
        setTotalPlants(0);
      }
    } catch (error) {
      console.error('Error fetching plantations:', error);
      setApiError(error.message);
      // Set defaults on error
      setTotalPlantations(0);
      setTotalPlants(0);
    } finally {
      setApiLoading(false);
    }
  }, [API_BASE_URL, filters]);
  useEffect(() => {
    const loadGeoJson = async () => {
      try {
        const [provincesResponse, districtsResponse] = await Promise.all([
          fetch(`${process.env.PUBLIC_URL}/assets/provinces_pakistan.geojson`),
          fetch(`${process.env.PUBLIC_URL}/assets/Districts.geojson`)
        ]);
        const provincesData = await provincesResponse.json();
        const districtsData = await districtsResponse.json();
        setProvinces(provincesData);
        setDistricts(districtsData);
      } catch (err) {
        console.error("Error loading GeoJSON:", err);
      } finally {
        setLoading(false);
      }
    };
    
    loadGeoJson();
    fetchFilterOptions();
  }, [fetchFilterOptions]);

  useEffect(() => {
    fetchPlantations();
  }, [fetchPlantations]);

  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      category: "",
      province: "",
      district: "",
      tehsil: "",
      yearOfPlantation: "",
      plantationCampaign: "",
      plantationType: "",
      siteType: ""
    });
  };

 return (
    <div className="col-xxl-12">
      <MapFilters 
        filters={filters}
        filterOptions={filterOptions}
        onFilterChange={handleFilterChange}
        onClearFilters={clearFilters}
      />
      
      <div className="card " ref={mapCardRef}>
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h6 className="fw-bold mb-0">Plantation Spatial Distribution
              <span className="badge bg-success ms-2">
              {/* Safely render the numbers */}
              {typeof totalPlantations === 'number' ? totalPlantations.toLocaleString() : '0'} Plantations
              </span>
              <span className="badge bg-primary ms-2">
                {typeof totalPlants === 'number' ? totalPlants.toLocaleString() : '0'} Plants
              </span>
              <span className="badge bg-info ms-2">
                Clustering: {isClusteringEnabled ? 'ON' : 'OFF'}
              </span>
            </h6>
            <div className="d-flex align-items-center gap-2">
              {apiLoading && (
                <span className="badge bg-warning">Loading Plantations...</span>
              )}
              {apiError && (
                <span className="badge bg-danger" title={apiError}>API Error</span>
              )}
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1"
                onClick={toggleFullscreen}
                title={isFullscreen ? 'Exit Full Screen' : 'View Full Screen'}
              >
                <Icon icon={isFullscreen ? 'solar:quit-full-screen-linear' : 'solar:full-screen-linear'} />
                {isFullscreen ? 'Exit Full Screen' : 'Full Screen'}
              </button>
            </div>
          </div>

          <div ref={mapWrapperRef} className="mt-2" style={{ height: isFullscreen ? 'calc(100vh - 140px)' : '900px', borderRadius: '8px', overflow: 'hidden', position: 'relative' }}>
            {loading ? (
              <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}>
                <Skeleton height="100%" />
              </div>
            ) : (
              <MapContainer 
                ref={mapRef}
                style={{ height: '100%', width: '100%' }} 
                scrollWheelZoom={true}
                preferCanvas={true}
                maxZoom={18}
                maxBounds={[
                  [37.0970, 60.9386],
                  [23.6345, 77.0859]
                ]}
                maxBoundsViscosity={1.0}
                minZoom={5}
              >
                <MapLegend />
                <MapSetBounds geojson={provinces} />
                
                {/* Clustering Control */}
                <MapClusterControl 
                  isClusteringEnabled={isClusteringEnabled}
                  onClusteringChange={handleClusteringChange}
                  plantations={plantations}
                />
                
                {/* Clustering Group */}
                <MapClusterGroup 
                  plantations={plantations}
                  isClusteringEnabled={isClusteringEnabled}
                />
                
                <TileLayer
                  url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                  attribution='Tiles © Esri'
                />

                {/* {showLabels && ( */}
                  <TileLayer
                    url="https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places_Alternate/MapServer/tile/{z}/{y}/{x}"
                    attribution='Tiles © Esri'
                  />
                {/* )} */}

                {provinces && (
                  <>
                    <GeoJSON
                      data={provinces}
                      style={(feature) => {
                        const index = provinces.features.indexOf(feature);
                        const colors = ["#1d3d02ff","#a7aa08ff","#032686ff","#8f0101ff","#041755ff","#043450ff","#074a4eff"];
                        const color = colors[index % colors.length];
                        return {
                          color: color,
                          weight: 2,
                          opacity: 1,
                          fillOpacity: 0.5,
                          fillColor: color
                        };
                      }}
                    />
                    <MapProvinceLabels geojson={provinces} />
                  </>
                )}

                {districts && (
                  <>
                    <GeoJSON
                      data={districts}
                      style={() => ({
                        color: '#000000',
                        weight: 1,
                        opacity: 1,
                        fillOpacity: 0
                      })}
                    />
                    <MapDistrictLabels geojson={districts} />
                  </>
                )}

                {/* Show individual markers only when clustering is disabled */}
                {!isClusteringEnabled && plantations.map((plantation, index) => (
                  <MapApiPinpoint 
                    key={plantation.plantationKey || index} 
                    plantation={plantation}
                    index={index}
                  />
                ))}
              </MapContainer>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MapComponent;